import React, { useContext } from 'react';
import { useForm } from 'react-hook-form';
import {
  Badge,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
  useToast,
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';

import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { HttpContext } from '../../context/HttpContext';
import { getErrors, getMessage } from '../../util';

const SupplierCompanyEditForm = ({
  user,
  suppliers,
  updateUser,
  onClose,
  canEditEmail,
  canEditNotificationEmails,
}) => {
  const {
    officialRegisteredName,
    email,
    notificationEmail1,
    notificationEmail2,
    notificationEmail3,
    notificationEmail4,
  } = user;

  const validationSchema = Yup.object().shape({
    officialRegisteredName: Yup.string().required('This is mandatory field'),
    // Email can be edited only by Finance Manager; for other roles it is read-only, so don't require it.
    email: canEditEmail
      ? Yup.string()
          .email('This must be a valid email')
          .required('This is mandatory field')
      : Yup.string().email('This must be a valid email').nullable().notRequired(),
    notificationEmail1: Yup.string()
      .email('This must be a valid email')
      .nullable()
      .notRequired(),
    notificationEmail2: Yup.string()
      .email('This must be a valid email')
      .nullable()
      .notRequired(),
    notificationEmail3: Yup.string()
      .email('This must be a valid email')
      .nullable()
      .notRequired(),
    notificationEmail4: Yup.string()
      .email('This must be a valid email')
      .nullable()
      .notRequired(),
  });
  const { register, handleSubmit, formState, setError, reset } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });
  const { errors, isSubmitting, isDirty, isValid } = formState;
  const toast = useToast();
  const { authAxios } = useContext(HttpContext);

  const onSubmit = async (values) => {
    try {
      const payload = { ...values };
      if (!canEditEmail) {
        delete payload.email;
      }
      if (!canEditNotificationEmails) {
        delete payload.notificationEmail1;
        delete payload.notificationEmail2;
        delete payload.notificationEmail3;
        delete payload.notificationEmail4;
      }

      const res = await authAxios.patch(`users/suppliers/${user.id}`, payload);
      // PATCH response may not contain all user fields (e.g. notification emails).
      // Merge what we submitted + what API returned to keep UI in sync without a full page refresh.
      const updatedUser = { ...user, ...payload, ...(res?.data ?? {}) };
      updateUser(updatedUser);
      reset(updatedUser);
      onClose();
    } catch (onError) {
      console.log(onError);
      const errors = getErrors(onError);
      if (
        errors &&
        errors.officialRegisteredName &&
        errors.officialRegisteredName.errors
      ) {
        const message = errors.officialRegisteredName.errors[0];
        setError('officialRegisteredName', { type: 'server', message });
      } else if (errors && errors.email && errors.email.errors) {
        const message = errors.email.errors[0];
        setError('email', { type: 'server', message });
      } else {
        const message = getMessage(onError);
        if (message) {
          toast({
            position: 'top-left',
            description: message,
            status: 'error',
            duration: 6000,
            isClosable: true,
          });
        }
      }
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControl isInvalid={errors.officialRegisteredName} mb={[4, 4, 8]}>
          <FormLabel htmlFor="emailHtmlFor">Official Registered Name</FormLabel>
          <Input
            isInvalid
            name="officialRegisteredName"
            id="officialRegisteredName"
            defaultValue={officialRegisteredName}
            placeholder="Enter name"
            focusBorderColor="black"
            errorBorderColor="error.500"
            {...register('officialRegisteredName')}
          />
          <FormErrorMessage>
            {errors.officialRegisteredName?.message || errors.officialRegisteredName}
          </FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={errors.email} mb={[4, 4, 8]}>
          <FormLabel htmlFor="emailHtmlFor">Email Address</FormLabel>
          <Input
            isInvalid
            disabled={!canEditEmail}
            color={!canEditEmail ? '#c3c1c1' : undefined}
            name="email"
            id="email"
            defaultValue={email}
            placeholder="Enter email address"
            focusBorderColor="black"
            errorBorderColor="error.500"
            {...register('email')}
          />
          <FormErrorMessage>{errors.email?.message || errors.email}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={errors.notificationEmail1} mb={[4, 4, 8]}>
          <FormLabel htmlFor="notificationEmail1HtmlFor">
            Notification Email (1)
          </FormLabel>
          <Input
            isInvalid
            disabled={!canEditNotificationEmails}
            color={!canEditNotificationEmails ? '#c3c1c1' : undefined}
            name="notificationEmail1"
            id="notificationEmail1"
            defaultValue={notificationEmail1}
            placeholder="Enter email address"
            focusBorderColor="black"
            errorBorderColor="error.500"
            {...register('notificationEmail1')}
          />
          <FormErrorMessage>
            {errors.notificationEmail1?.message || errors.notificationEmail1}
          </FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={errors.notificationEmail2} mb={[4, 4, 8]}>
          <FormLabel htmlFor="notificationEmail2HtmlFor">
            Notification Email (2)
          </FormLabel>
          <Input
            isInvalid
            disabled={!canEditNotificationEmails}
            color={!canEditNotificationEmails ? '#c3c1c1' : undefined}
            name="notificationEmail2"
            id="notificationEmail2"
            defaultValue={notificationEmail2}
            placeholder="Enter email address"
            focusBorderColor="black"
            errorBorderColor="error.500"
            {...register('notificationEmail2')}
          />
          <FormErrorMessage>
            {errors.notificationEmail2?.message || errors.notificationEmail2}
          </FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={errors.notificationEmail3} mb={[4, 4, 8]}>
          <FormLabel htmlFor="notificationEmail3HtmlFor">
            Notification Email (3)
          </FormLabel>
          <Input
            isInvalid
            disabled={!canEditNotificationEmails}
            color={!canEditNotificationEmails ? '#c3c1c1' : undefined}
            name="notificationEmail3"
            id="notificationEmail3"
            defaultValue={notificationEmail3}
            placeholder="Enter email address"
            focusBorderColor="black"
            errorBorderColor="error.500"
            {...register('notificationEmail3')}
          />
          <FormErrorMessage>
            {errors.notificationEmail3?.message || errors.notificationEmail3}
          </FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={errors.notificationEmail4} mb={[4, 4, 8]}>
          <FormLabel htmlFor="notificationEmail4HtmlFor">
            Notification Email (4)
          </FormLabel>
          <Input
            isInvalid
            disabled={!canEditNotificationEmails}
            color={!canEditNotificationEmails ? '#c3c1c1' : undefined}
            name="notificationEmail4"
            id="notificationEmail4"
            defaultValue={notificationEmail4}
            placeholder="Enter email address"
            focusBorderColor="black"
            errorBorderColor="error.500"
            {...register('notificationEmail4')}
          />
          <FormErrorMessage>
            {errors.notificationEmail4?.message || errors.notificationEmail4}
          </FormErrorMessage>
        </FormControl>

        <Box>
          <Text fontWeight="bold" mb="8px">
            Currency
          </Text>
          {suppliers.map((supp) => (
            <Badge
              key={supp.id}
              variant="solid"
              bg="transparent"
              pt="1"
              pb="1"
              px="2"
              mr="1"
              mb="1"
              fontSize="13px"
              fontWeight="400"
              color="#2b2b2b"
            >
              <CheckIcon mr={1} /> {supp.currency.code} - {supp.currency.name}
            </Badge>
          ))}
        </Box>

        <>
          <Box display="flex" justifyContent="flex-end" mt={20} mb={20}>
            <Button
              type="submit"
              size="lg"
              colorScheme="brand"
              disabled={!isDirty || !isValid || isSubmitting}
              isLoading={isSubmitting}
              textTransform="initial"
              _hover={{ bg: '#000' }}
              borderRadius="10px"
              mr="10px"
              //disabled={disableForm}
            >
              Submit
            </Button>
            <Button
              variant="ghost"
              size="lg"
              colorScheme="brand"
              textTransform="initial"
              borderRadius="10px"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </Box>
        </>
      </form>
    </>
  );
};
export default SupplierCompanyEditForm;
